import { defineMessages } from 'react-intl';

export const scope = 'app.containers.ApprovalRules';

/**
 * recruiter-ui uses the legacy FormatJS extraction: manual dot-notation ids, and
 * `description` as the in-code record of what a string means. Both are required
 * here — an omitted id silently never reaches the translation file.
 *
 * Terminology is "rule", not "flow" (AXHE-4015, renamed per Michaela). The design
 * handoff predates that rename and still says "flow" throughout; the copy here
 * deliberately does not.
 */
export default defineMessages({
  // The surface is "Approvals" (AXHE-4472); "Approval rules" is now the name of
  // what one tab holds, not of the page. A separate id rather than a reworded
  // `pageTitle`, because the two say different things and a translated catalog
  // carries the old sentence forward under an unchanged id.
  pageHeading: {
    id: `${scope}.pageHeading`,
    defaultMessage: 'Approvals',
    description:
      'Title of the page where approvals are configured and reviewed; also the browser tab title',
  },
  pageSubtitle: {
    id: `${scope}.pageSubtitle`,
    defaultMessage:
      'Opening approvals and offer approvals allow more control over when an opening can become active or an offer can be extended by requiring it to be reviewed and approved by an assigned approver group.',
    description:
      'Explains, under the page title, what approvals do and why an admin would configure them',
  },
  backToSettings: {
    id: `${scope}.backToSettings`,
    defaultMessage: 'Back to settings',
    description:
      'Link above the page title, returning to the account settings area this page is reached from',
  },
  pageTitle: {
    id: `${scope}.pageTitle`,
    defaultMessage: 'Approval rules',
    description:
      'Names the rules that decide when an offer or opening needs sign-off; the accessible name of the list while it loads',
  },
  approverRulesTab: {
    id: `${scope}.approverRulesTab`,
    defaultMessage: 'Approver rules',
    description:
      'Tab holding the rules that decide when an offer or opening needs sign-off',
  },
  // Names the segmented control the two entries below sit in. A group of toggle
  // buttons has no visible heading, so this is the only thing telling a screen
  // reader what the pair chooses between. "Type" is the word the table's own
  // column uses for the same distinction.
  subjectTypeLabel: {
    id: `${scope}.subjectTypeLabel`,
    defaultMessage: 'Rule type',
    description:
      'Accessible name of the segmented control choosing whether the table lists offer rules or opening rules',
  },
  // The two ids say "tab" and the control is now a segmented toggle. Kept as
  // they are: the words themselves have not changed, and reminting an id
  // orphans the sentence in every translated catalog that already carries it.
  offersTab: {
    id: `${scope}.offersTab`,
    defaultMessage: 'Offers',
    description: 'Filter showing only rules that gate offers to candidates',
  },
  openingsTab: {
    id: `${scope}.openingsTab`,
    defaultMessage: 'Openings',
    description: 'Filter showing only rules that gate job openings',
  },
  searchLabel: {
    id: `${scope}.searchLabel`,
    defaultMessage: 'Search rules',
    description: 'Label for the input that filters the rule list by name',
  },
  // Names the whole thing being created, because this button is the only entry
  // point to authoring one and "rule" alone does not say what kind. Sentence
  // case: the mock title-cases it, but every other button in the product is
  // sentence case and the design system says so explicitly.
  createRule: {
    id: `${scope}.createRule`,
    defaultMessage: 'Create approval rule',
    description:
      'Split button, with a caret, that opens the menu for starting a new approval rule',
  },
  levelCount: {
    id: `${scope}.levelCount`,
    defaultMessage:
      '{count, plural, one {# approval level} other {# approval levels}}',
    description:
      'How many sequential rounds of sign-off a rule requires before it is satisfied',
  },

  // --- table columns (AXHE-4464) ---
  // Design Requirements §2 heads the first column "Rules". Singular here: every
  // other header in the row names one cell's contents ("Type", "Trigger",
  // "Enabled"), and "Approvers" is plural only because a cell holds several.
  // A header is a label for the value under it, and the value is one rule.
  // The list's own loading state. Distinct from `pageTitle`, which now names the
  // scroll region and the table: three elements answering to "Approval rules"
  // would have a screen reader say it three times on the way into one table.
  loadingRules: {
    id: `${scope}.loadingRules`,
    defaultMessage: 'Loading approval rules',
    description:
      'Accessible name of the spinner shown in the table body while the rule list is being fetched',
  },
  columnRule: {
    id: `${scope}.columnRule`,
    defaultMessage: 'Rule',
    description: 'Column header over the name of each approval rule',
  },
  columnType: {
    id: `${scope}.columnType`,
    defaultMessage: 'Type',
    description:
      'Column header over the badge saying whether a rule gates offers or openings',
  },
  columnTrigger: {
    id: `${scope}.columnTrigger`,
    defaultMessage: 'Trigger',
    description:
      'Column header over the summary of what makes a rule or one of its levels apply',
  },
  columnApprovers: {
    id: `${scope}.columnApprovers`,
    defaultMessage: 'Approvers',
    description: 'Column header over the people who can sign off',
  },
  columnEnabled: {
    id: `${scope}.columnEnabled`,
    defaultMessage: 'Enabled',
    description:
      'Column header over the switch turning a rule on and off. Names the state, not the act — the cell holds a switch, and the header labels what it reports',
  },
  // The actions column shows two icon buttons and no visible header. The header
  // still exists for a screen reader reading the table by column, which is the
  // one context where an unnamed column is a gap rather than a clean design.
  columnActions: {
    id: `${scope}.columnActions`,
    defaultMessage: 'Actions',
    description:
      'Accessible-only column header over the edit and delete buttons on each rule row',
  },
  typeOffer: {
    id: `${scope}.typeOffer`,
    defaultMessage: 'Offer',
    description:
      'Badge in the Type column marking a rule that gates offers sent to candidates',
  },
  typeOpening: {
    id: `${scope}.typeOpening`,
    defaultMessage: 'Opening',
    description:
      'Badge in the Type column marking a rule that gates job openings',
  },
  editRule: {
    id: `${scope}.editRule`,
    defaultMessage: 'Edit rule',
    description:
      'Icon button on a rule row that opens the panel for changing that rule',
  },
  // Wraps the row's visible label rather than replacing it, so the accessible
  // name is a superset of what a speech-input user can read — WCAG 2.5.3 (Label
  // in Name). "Edit level {number}" was a replacement: the row reads
  // "Level 2 · Finance sign-off" and answered to "Edit level 2", so saying what
  // you see matched nothing.
  //
  // Still names the level rather than the rule, which is the reason this message
  // exists at all: four rows each open the panel on a different card, and one
  // "Edit rule" on all of them makes that difference invisible.
  editLevel: {
    id: `${scope}.editLevel`,
    defaultMessage: 'Edit {level}',
    description:
      'Accessible name of the control on a level row, which opens the rule panel at that level. {level} is the row\'s own visible label, so the name contains it rather than replacing it',
  },
  // Level rows are numbered by position, and a name is optional on a level —
  // most rules leave it blank, so the number has to carry the row on its own.
  levelRow: {
    id: `${scope}.levelRow`,
    defaultMessage: 'Level {number}',
    description: 'Label of a nested level row, when the level has no name',
  },
  levelRowNamed: {
    id: `${scope}.levelRowNamed`,
    defaultMessage: 'Level {number} · {name}',
    description:
      'Label of a nested level row, pairing its position with the name the admin gave it',
  },

  // --- trigger summary (AXHE-4464 §2) ---
  // What makes a rule, or one of its levels, apply. Read out of the stored
  // condition rather than fetched: `Approvals::Condition` is the authority on
  // what a condition means, and the operator words are already translated above
  // for the condition builder. Sentence fragments, not sentences — they sit in a
  // table cell, so no terminal punctuation.
  triggerAllOffers: {
    id: `${scope}.triggerAllOffers`,
    defaultMessage: 'All offers',
    description:
      'Trigger summary for a rule with no conditions, which therefore gates every offer',
  },
  triggerAllOpenings: {
    id: `${scope}.triggerAllOpenings`,
    defaultMessage: 'All openings',
    description:
      'Trigger summary for a rule with no conditions, which therefore gates every opening',
  },
  triggerAlways: {
    id: `${scope}.triggerAlways`,
    defaultMessage: 'Always',
    description:
      'Trigger summary for a level with no conditions, which therefore applies whenever the rule matched',
  },
  triggerPosition: {
    id: `${scope}.triggerPosition`,
    defaultMessage: 'Position: {list}',
    description:
      'Trigger summary naming the positions a rule or level is scoped to',
  },
  triggerLocation: {
    id: `${scope}.triggerLocation`,
    defaultMessage: 'Location: {list}',
    description:
      'Trigger summary naming the locations a rule or level is scoped to',
  },
  // Location groups are stored by id rather than name, so they can be counted
  // here but not named. Used when a condition selects groups and no individual
  // locations, where there is no name to lead with.
  triggerLocationGroups: {
    id: `${scope}.triggerLocationGroups`,
    defaultMessage:
      'Location: {count, plural, one {# location group} other {# location groups}}',
    description:
      'Trigger summary for a scope made up of location groups, which are stored by identifier and so cannot be named here',
  },
  triggerOpenings: {
    id: `${scope}.triggerOpenings`,
    defaultMessage:
      '{count, plural, one {# selected opening} other {# selected openings}}',
    description:
      'Trigger summary for a scope naming specific openings, which are stored by identifier and so cannot be named here',
  },
  triggerMore: {
    id: `${scope}.triggerMore`,
    defaultMessage: '{names} +{count}',
    description:
      'Shortens a long list in the trigger summary to the first few names plus how many are left',
  },
  triggerFieldValue: {
    id: `${scope}.triggerFieldValue`,
    defaultMessage: '{field} {operator} {value}',
    description:
      'Trigger summary for a condition written about a field, read left to right as a sentence — for example "Sign-on bonus greater than 10000"',
  },
  triggerFieldBare: {
    id: `${scope}.triggerFieldBare`,
    defaultMessage: '{field} {operator}',
    description:
      'Trigger summary for a condition whose comparison takes no value, such as "Cost Code is present"',
  },
  triggerPayOutsideRange: {
    id: `${scope}.triggerPayOutsideRange`,
    defaultMessage: 'Compensation: outside pay range',
    description:
      "Trigger summary for a pay condition firing when the offer is below or above the opening's posted range",
  },
  triggerPayAboveRange: {
    id: `${scope}.triggerPayAboveRange`,
    defaultMessage: 'Compensation: above pay range',
    description:
      "Trigger summary for a pay condition firing when the offer is above the top of the opening's posted range",
  },
  triggerPayBelowRange: {
    id: `${scope}.triggerPayBelowRange`,
    defaultMessage: 'Compensation: below pay range',
    description:
      "Trigger summary for a pay condition firing when the offer is below the bottom of the opening's posted range",
  },
  triggerPayOver: {
    id: `${scope}.triggerPayOver`,
    defaultMessage: 'Compensation: over {amount}',
    description:
      'Trigger summary for a pay condition firing above an amount the admin typed in',
  },
  triggerPayUnder: {
    id: `${scope}.triggerPayUnder`,
    defaultMessage: 'Compensation: under {amount}',
    description:
      'Trigger summary for a pay condition firing below an amount the admin typed in',
  },
  triggerPayOutsideAmounts: {
    id: `${scope}.triggerPayOutsideAmounts`,
    defaultMessage: 'Compensation: outside {min}–{max}',
    description:
      'Trigger summary for a pay condition firing outside a band the admin typed in',
  },
  // Every condition type the current editor writes has a phrasing above, so this
  // only appears for a rule authored against a build this one predates, or
  // through the API. Says how many rather than staying silent: an empty Trigger
  // cell on a live gate reads as "this rule catches nothing".
  triggerUnnamedConditions: {
    id: `${scope}.triggerUnnamedConditions`,
    defaultMessage:
      '{count, plural, one {# condition} other {# conditions}}',
    description:
      'Trigger summary fallback, counting conditions this page has no wording for rather than leaving the cell empty',
  },
  triggerAndMore: {
    id: `${scope}.triggerAndMore`,
    defaultMessage:
      '{summary} +{count, plural, one {# more condition} other {# more conditions}}',
    description:
      'Appended to a trigger summary that describes only some of the conditions, so the row admits it is showing less than the rule holds',
  },
  // Wraps one half of a single-level rule's combined trigger when that half is an
  // "or" of several conditions, so the "and" joining the rule's scope to its
  // level does not rebind the looser "or" — "(Position: Driver or Location:
  // Dallas) and Compensation: over 50,000" rather than a flat join that reads as
  // "Position or (Location and Compensation)".
  triggerGroup: {
    id: `${scope}.triggerGroup`,
    defaultMessage: '({group})',
    description:
      'Parenthesises a group of "or" conditions inside a combined trigger summary so the surrounding "and" cannot be misread as binding tighter',
  },
  approverCount: {
    id: `${scope}.approverCount`,
    defaultMessage: '{count, plural, one {# approver} other {# approvers}}',
    description: 'How many people can sign off at a single round of approval',
  },
  disabledRule: {
    id: `${scope}.disabledRule`,
    defaultMessage: 'Off',
    description:
      'Badge on a rule that is configured but currently not gating anything',
  },
  disabledLevel: {
    id: `${scope}.disabledLevel`,
    defaultMessage: 'Off',
    description:
      'Badge on a level the rule skips. Separate from the rule badge with the same wording because the two label different objects and a translation may need to agree with a different noun',
  },
  managedElsewhere: {
    id: `${scope}.managedElsewhere`,
    defaultMessage: 'Managed on Opening Approvals',
    description:
      'Badge on a rule that was carried over from the older approvals page and can only be edited there',
  },
  managedElsewhereHint: {
    id: `${scope}.managedElsewhereHint`,
    defaultMessage:
      'This rule came from the Opening Approvals page. Edit it there — changes made here would be overwritten.',
    description:
      'Explains why a carried-over rule cannot be edited on this page and where to edit it instead',
  },
  // An account with no rules and a search that matched nothing are two states,
  // not one (AXHE-4468). Design Requirements §2 gives a single "No approval
  // rules found." covering both; `layout-composition` is explicit that they do
  // not collapse — "Empty state (no data) and search/filter zero-result state
  // are separate cases — provide distinct messaging for each" — and the two
  // genuinely have different ways out, one recoverable by clearing the search
  // and the other needing a rule created. Keep both. The doc's wording is also
  // a title with a full stop, which `copywriting-voice` does not allow.
  emptyTitle: {
    id: `${scope}.emptyTitle`,
    defaultMessage: 'No approval rules yet',
    description:
      'Heading shown when an account has not configured any approval rules',
  },
  emptyBody: {
    id: `${scope}.emptyBody`,
    defaultMessage:
      'Create a rule to require sign-off before an offer or opening goes out.',
    description:
      'Explains what creating an approval rule accomplishes, shown when none exist',
  },
  noSearchResultsTitle: {
    id: `${scope}.noSearchResultsTitle`,
    defaultMessage: 'No results for "{term}"',
    description:
      'Heading shown when a rule name search matched nothing, naming what was searched',
  },
  noSearchResultsBody: {
    id: `${scope}.noSearchResultsBody`,
    defaultMessage: 'Check the spelling or clear the search to see every rule.',
    description: 'Tells the user how to recover from a search that found nothing',
  },
  clearSearch: {
    id: `${scope}.clearSearch`,
    defaultMessage: 'Clear search',
    description:
      'Button in the no-results state that empties the search field, bringing the whole rule list back',
  },
  // The search box's own clear button, which `SearchInput` otherwise names from
  // `globalMessages.clearSearch` — also "Clear search", and also on screen
  // whenever the field has text. Two controls with one accessible name doing one
  // job is ambiguous for anyone navigating by name, and it made
  // `getByRole('button', { name: 'Clear search' })` match two elements. Named
  // for the thing being searched, matching `searchLabel` above; the empty
  // state's wording is visible copy and stays as it is (#4497).
  clearSearchField: {
    id: `${scope}.clearSearchField`,
    defaultMessage: 'Clear rule search',
    description:
      'Accessible name of the button inside the search box that empties it, distinct from the no-results state button that does the same job',
  },
  loadError: {
    id: `${scope}.loadError`,
    defaultMessage: "We couldn't load your approval rules. Try again.",
    description:
      'Shown when the request for the rule list failed, with the recovery action',
  },
  retry: {
    id: `${scope}.retry`,
    defaultMessage: 'Try again',
    description: 'Button that re-requests the rule list after a failure',
  },

  // --- editor panel ---
  // The two items name the entity, not the rule: the button above them already
  // said "approval rule", so repeating it here would read "Create approval rule
  // › Offer rule". What the menu is actually asking is which entity the rule
  // gates, and a rule's subject type is fixed at creation — so this choice is
  // the one thing the menu exists to make.
  createOfferRule: {
    id: `${scope}.createOfferRule`,
    defaultMessage: 'Offer',
    description:
      'Menu choice that starts a rule gating offers sent to candidates',
  },
  createOpeningRule: {
    id: `${scope}.createOpeningRule`,
    defaultMessage: 'Opening',
    description: 'Menu choice that starts a rule gating job openings',
  },
  // `subject` selects the noun on all four of these rather than each one being a
  // pair of ids: one rule table backs both filters, a rule's subject type is
  // fixed at creation, and the panel always knows which it has. The slot holds a
  // bare noun and the frame around it is fixed, so a translation is free to put
  // the slot wherever its own word order needs it. Same form `deleteRulePending`
  // uses, for the same reason.
  //
  // Sentence case, and the mock title-cases these (Design Requirements §3 has
  // "Create Offer Approval Rule"). AXHE-4466 already departed from the mock on
  // exactly this for the split button above — see `createRule` — so following the
  // mock here would put "Create Offer Approval Rule" in the footer of a panel
  // opened by a "Create approval rule" button.
  createRuleTitle: {
    id: `${scope}.createRuleTitle`,
    defaultMessage:
      'Create {subject, select, Funnel {opening} other {offer}} approval rule',
    description:
      'Heading of the panel where a new rule is written. {subject} is the rule\'s subject type, which decides whether the heading names offers or openings',
  },
  editRuleTitle: {
    id: `${scope}.editRuleTitle`,
    defaultMessage:
      'Edit {subject, select, Funnel {opening} other {offer}} approval rule',
    description:
      'Heading of the panel where an existing rule is changed. {subject} is the rule\'s subject type, which decides whether the heading names offers or openings',
  },
  // Edit only. The mock gives no intro on create, and a panel that opens empty
  // has nothing to describe that its own fields do not.
  //
  // "scope" is added to the mock's wording, which enumerates only "levels and
  // approvers". That was accurate for the panel the mock was drawn against; this
  // ticket makes the rule's scope editable here too, so the mock's list is now
  // short by the one thing that decides which offers the rule touches at all.
  editRuleIntro: {
    id: `${scope}.editRuleIntro`,
    defaultMessage:
      "Edit this {subject, select, Funnel {opening} other {offer}} approval rule's scope, levels and approvers.",
    description:
      'Sentence under the edit heading, saying what the panel changes. {subject} is the rule\'s subject type, which decides whether it names an offer or an opening rule',
  },
  ruleNameLabel: {
    id: `${scope}.ruleNameLabel`,
    defaultMessage: 'Rule name',
    description: 'Label for the field naming an approval rule',
  },
  // Design Requirements §3 words this "Rule name is required" (AXHE-4468). It
  // stays phrased as the action instead: `constraint-anti-pattern-guard`
  // prohibits an error that only describes what went wrong, and
  // `copywriting-voice`'s error table gives this exact pair — "Required field"
  // ✗ / "Enter a job title" ✓. §3 asks for two things, and the other one is
  // now built: the field is marked required on its label, which is where "is
  // required" is said. This message is the part that says what to do about it.
  ruleNameRequired: {
    id: `${scope}.ruleNameRequired`,
    defaultMessage: 'Enter a rule name',
    description:
      'Validation message when the rule name field was left empty, phrased as the action to take',
  },
  // --- rule scope (AXHE-4467) ---
  // A noun phrase, not a question: "Apply this rule to" names the choice the
  // radios make. The two options are the only two states a rule's scope has —
  // conditions that always match, or conditions that select.
  scopeLegend: {
    id: `${scope}.scopeLegend`,
    defaultMessage: 'Apply this rule to',
    description:
      'Label for the choice between gating every offer or opening and gating only the ones matching conditions',
  },
  scopeAll: {
    id: `${scope}.scopeAll`,
    defaultMessage: 'All {subject, select, Funnel {openings} other {offers}}',
    description:
      'Choice that applies the rule to every offer or opening, with no conditions. {subject} is the rule\'s subject type',
  },
  scopeConditional: {
    id: `${scope}.scopeConditional`,
    defaultMessage:
      '{subject, select, Funnel {Openings} other {Offers}} that meet conditions',
    description:
      'Choice that applies the rule only to the offers or openings its conditions select. {subject} is the rule\'s subject type',
  },
  // Only with more than one level: a one-level rule has no "next level" to
  // describe, so the sentence would explain a sequence that does not exist.
  scopeAllLevels: {
    id: `${scope}.scopeAllLevels`,
    defaultMessage:
      "All {subject, select, Funnel {openings} other {offers}} pass through every level. Approval is required before moving to the next level, unless a level's own conditions exclude it.",
    description:
      'Explains what applying a multi-level rule to everything means: each level is asked in turn, and only a level whose own condition fails is skipped. {subject} is the rule\'s subject type',
  },
  // Phrased as the two ways out rather than as what is missing, which is what
  // `copywriting-voice` asks of an error — and here there genuinely are two, so
  // naming only the first would hide the one an admin who picked the wrong radio
  // actually wants.
  scopeNeedsCondition: {
    id: `${scope}.scopeNeedsCondition`,
    defaultMessage:
      'Add a condition, or apply this rule to all {subject, select, Funnel {openings} other {offers}}',
    description:
      'Validation message when a rule is set to apply only to what meets conditions but has none. {subject} is the rule\'s subject type',
  },
  // Said out loud because the alternative is a radio pair that lies. A rule
  // scoped by a position, location, opening or attribute condition genuinely does
  // not apply to everything, and this panel renders none of those types
  // (AXHE-4383) — so it can neither show what the scope is nor honestly offer to
  // drop it.
  scopeConditionsElsewhere: {
    id: `${scope}.scopeConditionsElsewhere`,
    defaultMessage:
      'This rule is scoped by conditions of a kind this panel does not show. They are kept as they are, so the rule cannot be applied to all {subject, select, Funnel {openings} other {offers}} from here.',
    description:
      "Read-only notice on a rule whose scope uses condition types the panel cannot render, saying that saving keeps them and that this is why the all-offers choice is unavailable. {subject} is the rule's subject type",
  },
  // The no-catalog twin of `scopeConditionsElsewhere`. When the field catalog
  // fails to load, the rule's own data-key scope rows are hidden too — the
  // builder shows its error state instead of them — so "All …" is withheld for a
  // reason that is recoverable, not permanent. Blaming the *kind* of condition
  // here would be wrong: these are exactly the kind the panel renders, and the
  // builder below offers a Retry that would bring them back. Named for the cause
  // the admin can act on.
  scopeConditionsUnavailable: {
    id: `${scope}.scopeConditionsUnavailable`,
    defaultMessage:
      "This rule's conditions can't be loaded, so it can't be applied to all {subject, select, Funnel {openings} other {offers}} from here yet. They are kept as they are.",
    description:
      "Read-only notice on the rule scope when the field catalog failed to load, so the rule's own conditions can't be shown and the all-offers choice is withheld until they load. Distinct from scopeConditionsElsewhere, which is about condition types the panel never renders. {subject} is the rule's subject type",
  },

  // Shown when the stored scope holds a "matches everything" row beside a
  // narrowing one, and the panel is on the conditional choice. Saving drops the
  // "everything" row — which is right for a conditional rule — but under OR logic
  // that row is what makes the *stored* rule gate every subject, so the drop
  // changes what fires. Two strings, chosen by the rule's condition logic: under
  // AND the row is redundant and the drop is only tidying; under OR it narrows,
  // which is the one the admin has to be told before it happens silently.
  scopeAlwaysDropped: {
    id: `${scope}.scopeAlwaysDropped`,
    defaultMessage:
      'This rule also has a condition that matches every {subject, select, Funnel {opening} other {offer}}. Saving removes it and keeps only the conditions below.',
    description:
      "Notice that an existing 'matches everything' scope row will be removed on save because the rule is set to apply only to what its conditions select, under AND logic where the row is redundant. {subject} is the rule's subject type",
  },
  scopeAlwaysDroppedNarrows: {
    id: `${scope}.scopeAlwaysDroppedNarrows`,
    defaultMessage:
      'This rule currently applies to every {subject, select, Funnel {opening} other {offer}}. Saving removes that and applies it only to the {subject, select, Funnel {openings} other {offers}} the conditions below select.',
    description:
      "Warning that an existing 'matches everything' scope row will be removed on save, and because the rule combines its scope conditions with OR that row is what makes it apply to everything today, so saving narrows what the rule gates. {subject} is the rule's subject type",
  },
  levelHeading: {
    id: `${scope}.levelHeading`,
    defaultMessage: 'Level {number}',
    description:
      'Heading of one round of sign-off within a rule, numbered by its order',
  },
  levelNameLabel: {
    id: `${scope}.levelNameLabel`,
    defaultMessage: 'Level name',
    description: 'Label for the field naming one round of sign-off',
  },
  levelOff: {
    id: `${scope}.levelOff`,
    defaultMessage:
      'This level is off, so the rule skips it. Saving keeps it off — levels are no longer switched on and off here.',
    description:
      'Read-only notice on a level that was switched off before per-level toggles were removed, saying that the rule ignores it and that saving does not change that',
  },
  approversLabel: {
    id: `${scope}.approversLabel`,
    defaultMessage: 'Approvers',
    description: 'Label for the people who can sign off at this round',
  },
  // Design Requirements §3 words this "Each level requires at least one
  // approver." (AXHE-4468). "needs" and "requires" say the same thing, so the
  // only real difference is the full stop, and `copywriting-voice`'s rule is
  // one sentence takes none, two or more take punctuation throughout — which
  // is exactly how this file already splits: the single-sentence ones below
  // carry no stop, `conditionFieldUnavailable` and `conditionOperatorUnavailable`
  // are two sentences and do. This is one sentence. Not worth orphaning five
  // translated catalogs to add a period the guide asks us not to have.
  approverRequired: {
    id: `${scope}.approverRequired`,
    defaultMessage: 'Each level needs at least one approver',
    description:
      'Validation message when a round of sign-off was left with nobody able to approve it',
  },
  offboardedApprovers: {
    id: `${scope}.offboardedApprovers`,
    defaultMessage:
      '{count, plural, one {# approver is no longer an active user} other {# approvers are no longer active users}} and will be removed from this level when you save',
    description:
      'Warning shown when a level lists approvers whose user accounts have been deactivated, so an admin knows that saving drops them',
  },
  addLevel: {
    id: `${scope}.addLevel`,
    defaultMessage: 'Add level',
    description: 'Button that appends another round of sign-off to the rule',
  },
  removeLevel: {
    id: `${scope}.removeLevel`,
    defaultMessage: 'Remove level',
    description: 'Button that deletes one round of sign-off from the rule',
  },
  reorderLevel: {
    id: `${scope}.reorderLevel`,
    defaultMessage: 'Reorder level {number}',
    description:
      'Accessible label for the handle that moves one round of sign-off earlier or later in the sequence. Says "reorder" rather than "drag" because the handle is operated by keyboard as well',
  },
  // Two labels, not one: creating a rule and saving an edit are different
  // actions, and the footer primary is the last thing read before either one
  // commits. `globalMessages.saveChanges` is not reused — it is "Save Changes",
  // and buttons are sentence case.
  //
  // The English matches `createRule` above, and the id deliberately does not:
  // that one opens a menu and this one writes the rule, which a translation may
  // well distinguish even where English does not.
  createRuleSubmit: {
    id: `${scope}.createRuleSubmit`,
    defaultMessage: 'Create approval rule',
    description: 'Button that writes a new approval rule',
  },
  saveRuleChanges: {
    id: `${scope}.saveRuleChanges`,
    defaultMessage: 'Save changes',
    description: 'Button that writes the edits made to an existing rule',
  },
  // Scoped to a level's conditions. The rule's own scope conditions are edited
  // in this panel now (AXHE-4467), and the ones this builder cannot render say
  // so where they are — see `scopeConditionsElsewhere`.
  conditionsPreserved: {
    id: `${scope}.conditionsPreserved`,
    defaultMessage:
      'Only data-key conditions are edited here. A level\'s other condition types are kept as they are.',
    description:
      "Clarifies that only data-key conditions are editable in this panel and that a level's other condition types are carried over untouched rather than being lost on save",
  },
  saveFailed: {
    id: `${scope}.saveFailed`,
    defaultMessage: "We couldn't save this rule. Try again.",
    description:
      'Shown when writing the rule failed, with the recovery action',
  },

  // --- row actions ---
  // "Enabled", matching the column header above it and the "Off" badge that
  // reports the same state. It read "Active" until review on #4599 pointed out
  // that the new column header made one row say three words for one concept —
  // header "Enabled", switch "Active", badge "Off" — where `copywriting-voice`
  // asks for one vocabulary across an experience. On/off is the switch's own
  // pairing, so the badge stays as it is.
  //
  // Reworded under the same id, per the recruiter-ui i18n rule. Note the
  // consequence: the 77 translated catalogs still hold their rendering of
  // "Active" until they are re-translated, which is the right trade here because
  // the two words name the same state.
  ruleActiveToggle: {
    id: `${scope}.ruleActiveToggle`,
    defaultMessage: 'Enabled',
    description:
      'Switch on a rule row controlling whether it currently gates anything. Matches the column header over it',
  },
  deleteRule: {
    id: `${scope}.deleteRule`,
    defaultMessage: 'Delete rule',
    description: 'Button that permanently removes an approval rule',
  },
  // Names the action, not the rule; the rule is named in the body sentence
  // instead. This is the design's, and nothing in the shell forces it — an
  // earlier revision of this dialog justified it by FDS `ModalHeader`'s
  // `noWrap`, which would have truncated a long account-authored rule name in
  // the heading, but that shell is gone and the heading here wraps. Recorded
  // because the reason is worth having on the record accurately: a short,
  // constant title reads as the action and leaves the rule name somewhere it
  // can wrap, which is a preference rather than a constraint (AXHE-4553).
  deleteRuleTitle: {
    id: `${scope}.deleteRuleTitle`,
    defaultMessage: 'Delete rule',
    description:
      'Heading of the confirmation asking whether to permanently remove an approval rule',
  },
  // Opens on the consequence, not on "Are you sure you want to" — the standard
  // in `.claude/skills/ux-design/copywriting-voice` names that opener as the
  // anti-pattern, and nothing here forces it: the name sits in the same
  // wrappable position either way. The prototype's own wording carries the
  // filler; AXHE-4553 holds that difference open for Design.
  deleteRuleCascade: {
    id: `${scope}.deleteRuleCascade`,
    defaultMessage:
      'This permanently removes <b>{name}</b> and {count, plural, one {its # level} other {all # levels}} within it.',
    description:
      'Body of the delete confirmation. Names the rule being deleted and what else is destroyed alongside it, so the scale of the deletion is clear before confirming. <b> marks the rule name, which is the one part an admin has to check before confirming',
  },
  // Two consequences, in the order an admin can act on them: the requests that
  // stop, and what then happens to the subjects behind them. The second sentence
  // is the one the design added — an approval that is cancelled does not hold
  // its subject back, so it goes out unapproved unless another rule catches it.
  //
  // `subject` selects the noun because one rule table backs both filters: a rule
  // is either offers or openings, never both, and the design requirements spell
  // the consequence "those offers/openings" precisely because the dialog serves
  // whichever one is open. Printing both nouns on a surface the admin has
  // already filtered would be vaguer than the rule itself is.
  deleteRulePending: {
    id: `${scope}.deleteRulePending`,
    defaultMessage:
      'Deleting this rule will <b>cancel {count, plural, one {# pending approval request} other {# pending approval requests}}</b> currently using it. If no other rule applies, {subject, select, Funnel {those openings} other {those offers}} will pass through as <b>not needing approval</b>.',
    description:
      'Warning that the offers or openings currently awaiting sign-off under this rule stop being gated by it, and go out unapproved unless another rule applies. {subject} is the rule\'s subject type, which decides whether the sentence names offers or openings. <b> marks the count and the outcome, the two parts that change what the admin decides',
  },
  // Its own line rather than the tail of the body sentence: the design puts the
  // permanence note under the warning so the warning is what the eye lands on,
  // and a sentence about levels is not where an admin looks for "this is final".
  //
  // Worded as the standard spells it rather than the prototype's "This action
  // cannot be undone." — same sentence, house contraction (AXHE-4553).
  deleteRuleUndone: {
    id: `${scope}.deleteRuleUndone`,
    defaultMessage: "This can't be undone.",
    description:
      'Footnote under the delete confirmation, stating that the deletion is permanent',
  },
  toggleFailed: {
    id: `${scope}.toggleFailed`,
    defaultMessage: "We couldn't change this rule. Try again.",
    description:
      'Shown when switching a rule active or inactive failed, with the recovery action',
  },
  deleteFailed: {
    id: `${scope}.deleteFailed`,
    defaultMessage: "We couldn't delete this rule. Try again.",
    description: 'Shown when removing a rule failed, with the recovery action',
  },
  // --- condition builder (AXHE-3929) ---
  conditionsLabel: {
    id: `${scope}.conditionsLabel`,
    defaultMessage: "Conditions",
    description:
      "Heading for the rules deciding whether this level of sign-off applies at all",
  },
  addCondition: {
    id: `${scope}.addCondition`,
    defaultMessage: "Add condition",
    description: "Button that adds another condition to this level",
  },
  removeCondition: {
    id: `${scope}.removeCondition`,
    defaultMessage: "Remove condition",
    description: "Button that deletes one condition from this level",
  },
  conditionFieldLabel: {
    id: `${scope}.conditionFieldLabel`,
    defaultMessage: "Field",
    description: "Label for the data the condition is written about",
  },
  conditionOperatorLabel: {
    id: `${scope}.conditionOperatorLabel`,
    defaultMessage: "Comparison",
    description: "Label for how the field is compared, such as greater than",
  },
  conditionValueLabel: {
    id: `${scope}.conditionValueLabel`,
    defaultMessage: "Value",
    description: "Label for the literal the field is compared against",
  },
  conditionCompareKeyLabel: {
    id: `${scope}.conditionCompareKeyLabel`,
    defaultMessage: "Compare with field",
    description:
      "Label for the second field this condition compares against instead of a fixed value",
  },
  compareToFieldToggle: {
    id: `${scope}.compareToFieldToggle`,
    defaultMessage: "Compare with another field",
    description:
      "Switches the condition from comparing against a typed-in value to comparing against a second field",
  },
  conditionNeedsField: {
    id: `${scope}.conditionNeedsField`,
    defaultMessage: "Choose a field",
    description: "Validation message when a condition has no field selected",
  },
  conditionNeedsOperator: {
    id: `${scope}.conditionNeedsOperator`,
    defaultMessage: "Choose a comparison",
    description: "Validation message when a condition has no comparison selected",
  },
  conditionNeedsValue: {
    id: `${scope}.conditionNeedsValue`,
    defaultMessage: "Enter a value to compare against",
    description: "Validation message when a condition has nothing to compare the field with",
  },
  conditionNeedsCompareKey: {
    id: `${scope}.conditionNeedsCompareKey`,
    defaultMessage: "Choose a field to compare with",
    description:
      "Validation message when a condition compares against another field but none was selected",
  },
  noComparableFields: {
    id: `${scope}.noComparableFields`,
    defaultMessage: "No other field has the same type as this one",
    description:
      "Explains why the compare-with-field list is empty: comparisons are only offered between fields holding the same kind of data",
  },
  conditionsLoading: {
    id: `${scope}.conditionsLoading`,
    defaultMessage: "Loading fields",
    description:
      "Shown while the list of fields a condition can be written about is still being fetched",
  },
  conditionsLoadError: {
    id: `${scope}.conditionsLoadError`,
    defaultMessage:
      "We couldn't load the fields conditions are written about. Conditions on this rule are unchanged.",
    description:
      "Shown when the field catalog failed to load: conditions cannot be edited, and the ones already on the rule are left as they are",
  },
  conditionFieldUnavailable: {
    id: `${scope}.conditionFieldUnavailable`,
    defaultMessage: "This field is no longer available. Choose another.",
    description:
      "Validation message when a saved condition names a field the account no longer has",
  },
  // One message for both stale states the server distinguishes — the compare
  // target is gone, or both fields still exist and a type changed under them.
  // The admin's move is the same either way, and `copywriting-voice` asks for the
  // action rather than the diagnosis. Worded to match
  // `conditionOperatorUnavailable`, which is the same shape one control along.
  conditionCompareKeyUnavailable: {
    id: `${scope}.conditionCompareKeyUnavailable`,
    defaultMessage:
      'This field can no longer be compared with the one above. Choose another.',
    description:
      "Validation message when a saved condition compares against a second field the account has removed, or whose type no longer matches the first field's",
  },
  conditionOperatorUnavailable: {
    id: `${scope}.conditionOperatorUnavailable`,
    defaultMessage: "This comparison no longer applies to this field. Choose another.",
    description:
      "Validation message when a saved condition uses a comparison the field's type does not allow, usually because the field's type changed",
  },

  // --- operator labels (AXHE-4473) ---
  // The server sends its own tokens (`greater_than`); these are what an admin
  // reads. Phrased to complete the sentence the row spells out left to right —
  // "Sign-on bonus / greater than / 10000" — so each one is a verb phrase in
  // lower case rather than a title-cased label.
  operatorEquals: {
    id: `${scope}.operatorEquals`,
    defaultMessage: 'equals',
    description:
      "Comparison choice: the field's value is exactly the value given",
  },
  operatorNotEquals: {
    id: `${scope}.operatorNotEquals`,
    defaultMessage: 'does not equal',
    description:
      "Comparison choice: the field's value is anything other than the value given",
  },
  operatorContains: {
    id: `${scope}.operatorContains`,
    defaultMessage: 'contains',
    description:
      "Comparison choice: the field's text includes the value given somewhere within it",
  },
  operatorGreaterThan: {
    id: `${scope}.operatorGreaterThan`,
    defaultMessage: 'greater than',
    description:
      "Comparison choice: the field's value is above the value given",
  },
  operatorLessThan: {
    id: `${scope}.operatorLessThan`,
    defaultMessage: 'less than',
    description:
      "Comparison choice: the field's value is below the value given",
  },
  operatorIsPresent: {
    id: `${scope}.operatorIsPresent`,
    defaultMessage: 'is present',
    description:
      'Comparison choice: the field has any value at all, whatever it is',
  },

  // --- how conditions combine (AXHE-3930's UI half) ---
  // Sentence case rather than `globalMessages.and` / `.or`, which are "AND" and
  // "OR" — those are used as inline connectors in older surfaces, and a pill is
  // sentence case.
  conditionLogicAnd: {
    id: `${scope}.conditionLogicAnd`,
    defaultMessage: 'And',
    description:
      'Connector between two conditions, meaning both of them have to be true for the level to apply',
  },
  conditionLogicOr: {
    id: `${scope}.conditionLogicOr`,
    defaultMessage: 'Or',
    description:
      'Connector between two conditions, meaning either one of them being true is enough for the level to apply',
  },
  conditionLogicToggle: {
    id: `${scope}.conditionLogicToggle`,
    defaultMessage:
      'Conditions on this level combine with {current}. Switch to {other}',
    description:
      'Accessible name of the connector between two conditions, saying how they currently combine and what clicking it changes them to',
  },
});
